/**
 * Utility functions for cryptographic operations
 */

/**
 * Hash an email address using SHA-256 for privacy
 * Uses Web Crypto API which is available in modern browsers
 * @param email - The email address to hash
 * @returns Promise with the hashed email as hex string
 */
export const hashEmail = async (email: string): Promise<string> => {
  // Normalize email: lowercase and trim
  const normalizedEmail = email.toLowerCase().trim();
  
  // Convert string to Uint8Array
  const encoder = new TextEncoder();
  const data = encoder.encode(normalizedEmail);
  
  // Hash using SHA-256
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  
  // Convert buffer to hex string
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  
  return hashHex;
};

/**
 * Create a deterministic document ID from an email
 * Useful for consistent document references
 * @param email - The email address
 * @returns A safe document ID
 */
export const createUserDocumentId = async (email: string): Promise<string> => {
  const hash = await hashEmail(email);
  // Use first 16 characters of hash for shorter IDs
  // Still provides sufficient uniqueness (16^16 possibilities)
  return `user_${hash.substring(0, 16)}`;
};

/**
 * Generate a session ID with timestamp and enhanced randomness
 * @returns A unique session ID
 */
export const generateSessionId = (): string => {
  const timestamp = Date.now();

  // Use crypto.randomUUID if available (modern browsers)
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    // Use UUID v4 for guaranteed uniqueness
    const uuid = crypto.randomUUID().replace(/-/g, '').substring(0, 16);
    return `session_${timestamp}_${uuid}`;
  }

  // Fallback: Use crypto.getRandomValues for better randomness
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    const array = new Uint8Array(8);
    crypto.getRandomValues(array);
    const random = Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
    return `session_${timestamp}_${random}`;
  }

  // Last resort: Use Math.random with longer string
  const random1 = Math.random().toString(36).substring(2, 15);
  const random2 = Math.random().toString(36).substring(2, 15);
  return `session_${timestamp}_${random1}${random2}`;
};

/**
 * Format a timestamp for file naming
 * @param date - Date object or timestamp
 * @returns Formatted string YYYYMMDD_HHMMSS
 */
export const formatTimestampForFile = (date: Date = new Date()): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  
  return `${year}${month}${day}_${hours}${minutes}${seconds}`;
};